import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandPalette, type CommandPaletteItem } from '@domcrm/ui';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import { fetchOrgClientsList, type OrgClientSummary } from '../../features/clients/api/clients.api';
import { listDocsSearchSections } from '../../features/docs-release/docs-release-search';
import { formatPhoneRuDisplay } from '../../shared/lib/phone-ru';
import { isRouteAllowed } from '../../shared/access/access';
import { isStaffPlatformMode } from '../../shared/access/staff-platform-mode';
import type { SuperadminOrgTenantWideUiGateContext } from '../../shared/access/tenant-staff-governance';
import { useAuth } from '../../shared/auth/use-auth';
import { resolveStaffNavSidebarSection, STAFF_NAV_CATALOG } from '../navigation/staff-route-catalog';
import { filterVisibleStaffNavItems, staffNavSectionTitle } from '../navigation/staff-nav-visibility';

/** Минимум символов для поиска клиентов в палитре (запрос к серверу). */
const CLIENT_SEARCH_MIN_CHARS = 2;
const CLIENT_SEARCH_DEBOUNCE_MS = 250;

/** Глобальная палитра Ctrl+K: клиенты (поиск по имени, телефону, почте), разделы меню и документация. */
export function StaffCommandPalette() {
  const { me, impersonateOrgId, bootstrapState, apiClient } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [clients, setClients] = useState<OrgClientSummary[]>([]);
  const caps = me?.capabilities;
  const canSearchClients = isRouteAllowed(caps, '/clients') && !isStaffPlatformMode(me, impersonateOrgId);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  useEffect(() => {
    const q = search.trim();
    if (!open || !canSearchClients || q.length < CLIENT_SEARCH_MIN_CHARS) {
      setClients([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchOrgClientsList(apiClient, { q, limit: 5 })
        .then((res) => {
          if (!cancelled) setClients(res.items);
        })
        .catch(() => {
          if (!cancelled) setClients([]);
        });
    }, CLIENT_SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [apiClient, canSearchClients, open, search]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      event.preventDefault();
      setOpen((prev) => !prev);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const items = useMemo<CommandPaletteItem[]>(() => {
    if (!open) return [];
    const platformMode = isStaffPlatformMode(me, impersonateOrgId);
    const orgBootstrap =
      bootstrapState.kind === 'authenticated_with_org_context' || bootstrapState.kind === 'impersonating_org';
    const saOrgWideGateCtx: SuperadminOrgTenantWideUiGateContext = { me, orgBootstrap };
    const visible = filterVisibleStaffNavItems(
      STAFF_NAV_CATALOG,
      caps,
      platformMode,
      saOrgWideGateCtx,
      me,
      impersonateOrgId
    );

    const out: CommandPaletteItem[] = visible.map((item) => {
      const sectionTitle = staffNavSectionTitle(resolveStaffNavSidebarSection(item, platformMode));
      return {
        label: `${sectionTitle} · ${item.label}`,
        value: `nav:${item.backendRouteKey} ${item.label} ${sectionTitle}`,
        onSelect: () => {
          setOpen(false);
          navigate(item.frontendPath);
        }
      };
    });

    for (const c of clients) {
      const phone = c.phone ? formatPhoneRuDisplay(c.phone) : '';
      const details = [phone, c.email].filter(Boolean).join(' · ');
      out.push({
        label: `Клиент · ${c.full_name || c.email}${details ? ` — ${details}` : ''}`,
        // В value — текущий запрос: клиенты уже найдены сервером и не должны отсеиваться локальным фильтром.
        value: `client:${c.id} ${search} ${c.full_name} ${c.phone ?? ''} ${c.email}`,
        onSelect: () => {
          setOpen(false);
          navigate(`${ROUTE_PATHS.tenantUsersClientsTab}?open_client=${c.id}`);
        }
      });
    }

    if (isRouteAllowed(caps, ROUTE_PATHS.docs)) {
      for (const section of listDocsSearchSections()) {
        const query = new URLSearchParams({ doc: section.docId });
        if (section.anchor) query.set('anchor', section.anchor);
        out.push({
          label: `Документация · ${section.docTitle}${section.heading ? ` — ${section.heading}` : ''}`,
          value: `docs:${section.docId}:${section.position} ${section.docTitle} ${section.heading ?? ''}`,
          /** Документация идёт после разделов меню в сортировке результатов. */
          rank: 1,
          onSelect: () => {
            setOpen(false);
            navigate(`${ROUTE_PATHS.docs}?${query.toString()}`);
          }
        });
      }
    }

    return out;
  }, [open, me, impersonateOrgId, bootstrapState, caps, navigate, clients, search]);

  return (
    <CommandPalette
      open={open}
      onOpenChange={setOpen}
      items={items}
      searchPlaceholder={canSearchClients ? 'Клиент (имя, телефон, почта), раздел меню, документация…' : 'Разделы меню и документация…'}
      emptyLabel="Ничего не найдено"
      dataTestId="staff-command-palette"
      search={search}
      onSearchChange={setSearch}
    />
  );
}
